import { useMemo } from "react";
import { useDataStore } from "@store/user_org";
import type { OrgClosure } from "../types/org";

/** Stable empty array so the hook selector keeps a constant reference. */
const EMPTY: OrgClosure[] = [];

/**
 * Org-level garage closures, managed in Paramètres → Dispo atelier.
 *
 * A closure either blocks online booking only (`full=false`, default) or fully
 * closes the garage (`full=true`, which also reddens the atelier plannings).
 * `annual=true` repeats every year. The online-booking engine enforces both
 * kinds; the plannings only react to `full` closures.
 *
 * Back-compat: the legacy flat `booking_closed_dates` is surfaced as
 * online-only closures (`full=false`) so existing blackout periods stop
 * reddening the planning without any manual migration.
 */
export function useOrgClosures(): OrgClosure[] {
  const closures = useDataStore((s) => s.orgData?.config?.booking_closures);
  const legacy = useDataStore((s) => s.orgData?.config?.booking_closed_dates);
  return useMemo(() => {
    if (closures && closures.length > 0) return closures;
    if (legacy && legacy.length > 0) {
      // Group consecutive legacy dates into ranges, online-only.
      return groupLegacyDates(legacy);
    }
    return EMPTY;
  }, [closures, legacy]);
}

/** Full garage closures only (the ones that redden the atelier plannings). */
export function useFullClosures(): OrgClosure[] {
  const all = useOrgClosures();
  return useMemo(() => all.filter((c) => c.full), [all]);
}

/** Whether a closure period covers ISO `date` (honouring ranges + annual). */
export function closureCoversDate(c: OrgClosure, date: string): boolean {
  const start = c.start;
  if (!start) return false;
  let end = c.end && c.end >= start ? c.end : start;
  if (c.annual) {
    const md = date.slice(5, 10);
    const ms = start.slice(5, 10);
    const me = end.slice(5, 10);
    return ms <= me ? md >= ms && md <= me : md >= ms || md <= me;
  }
  return date >= start && date <= end;
}

/** True when `date` is a FULL garage closure (planning shown closed/red). */
export function isDateClosed(date: string, closures: OrgClosure[]): boolean {
  return closures.some((c) => c.full && closureCoversDate(c, date));
}

/** Group a flat sorted list of legacy ISO dates into online-only closure ranges. */
function groupLegacyDates(dates: string[]): OrgClosure[] {
  const sorted = Array.from(new Set(dates)).sort();
  const out: OrgClosure[] = [];
  const dayAfter = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d + 1));
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
  };
  for (const d of sorted) {
    const last = out[out.length - 1];
    if (last && dayAfter(last.end || last.start) === d) {
      last.end = d;
      continue;
    }
    out.push({ id: `legacy_${d}`, start: d, end: d, full: false, annual: false });
  }
  return out;
}
